import classnames from 'classnames';
import InputGroup from 'components/LoginSignUp/InputGroup';
import PasswordInput from 'components/PasswordInput';
import RadioButton from 'components/RadioButton';
import useSortAndFormatCountryPhoneNumberCodes from 'hooks/useSortAndFormatCountryPhoneNumberCodes';
import SubmitButton from 'modules/authentication/components/common/SubmitButton';
import React from 'react';

import { Translator } from '../../Translator';

const CSS = require('./SignUpForm.less');

interface SignUpFormProps {
  onSubmit: (e: any) => void;
  onChange: (e: any) => void;
  onPhoneCountryCodeClick: (e: any) => void;
  countries: any[];
  signupButtonLoading: boolean;
  email: string;
  password: string;
  gender: undefined | 'man' | 'woman' | 'other';
  firstname: string;
  lastname: string;
  country: string;
  phoneCountryCode: string;
  phoneNumber: string;
  options: {
    gender?: boolean;
    firstname?: boolean;
    lastname?: boolean;
    country?: boolean;
    phoneCountryCode?: boolean;
    phoneNumber?: boolean;
  };
}

const renderCountries = (countries) => {
  return countries.map((country) => {
    return (
      <option key={country.countryCode} value={country.name}>
        {country.name}
      </option>
    );
  });
};

function CountryCodeSelect({ countries }) {
  const { popularCountries, otherCountries } =
    useSortAndFormatCountryPhoneNumberCodes({ countries });
  return (
    <>
      <optgroup label="Popular country codes">
        {popularCountries.map(({ label, value }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </optgroup>
      <hr />
      <optgroup label="Other country codes">
        {otherCountries.map(({ label, value }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </optgroup>
    </>
  );
}

const renderGenderOptions = (onClick, currentGender) => {
  return [
    <label key={'man'}>
      <RadioButton
        checked={currentGender === 'man'}
        value={'man'}
        name={'gender'}
        onClick={onClick}
        required
      />
      {Translator.translate('Forms.Gender.male')}
    </label>,
    <label key={'woman'}>
      <RadioButton
        checked={currentGender === 'woman'}
        value={'woman'}
        name={'gender'}
        onClick={onClick}
        required
      />
      {Translator.translate('Forms.Gender.female')}
    </label>,
    <label key={'other'}>
      <RadioButton
        checked={currentGender === 'other'}
        value={'other'}
        name={'gender'}
        onClick={onClick}
        required
      />
      {Translator.translate('Forms.Gender.other')}
    </label>,
  ];
};

function SignUpForm({
  email,
  country,
  phoneCountryCode,
  phoneNumber,
  firstname,
  lastname,
  gender,
  password,
  options,
  countries,
  signupButtonLoading,
  onSubmit,
  onChange,
  onPhoneCountryCodeClick,
}: SignUpFormProps) {
  return (
    <form className={CSS.form} onSubmit={onSubmit}>
      {options.gender && (
        <InputGroup>
          <label className={CSS.inputLabel}>
            {Translator.translate('Forms.Labels.gender', {}, true, 'Gender')}
          </label>
          <div className={CSS.radioButtons}>
            {renderGenderOptions(onChange, gender)}
          </div>
        </InputGroup>
      )}
      {options.firstname && (
        <InputGroup>
          <label className={CSS.inputLabel}>
            {Translator.translate(
              'Forms.Labels.firstName',
              {},
              true,
              'First name',
            )}
          </label>
          <input
            autoComplete={'given-name'}
            className={CSS.input}
            type={'text'}
            value={firstname}
            name={'firstname'}
            onChange={onChange}
            required
            placeholder={'First name'}
          />
        </InputGroup>
      )}
      {options.lastname && (
        <InputGroup>
          <label className={CSS.inputLabel}>
            {Translator.translate(
              'Forms.Labels.lastName',
              {},
              true,
              'Last name',
            )}
          </label>
          <input
            autoComplete={'family-name'}
            className={CSS.input}
            type={'text'}
            value={lastname}
            name={'lastname'}
            onChange={onChange}
            required
            placeholder={'Last name'}
          />
        </InputGroup>
      )}
      <InputGroup>
        <label className={CSS.inputLabel}>
          {Translator.translate('Forms.Labels.email', {}, true, 'Email')}
        </label>
        <input
          autoComplete={'email'}
          className={CSS.input}
          type={'email'}
          value={email}
          name={'email'}
          onChange={onChange}
          required
          placeholder={'Email'}
        />
      </InputGroup>
      <InputGroup>
        <label className={CSS.inputLabel}>
          {Translator.translate('Forms.Labels.password', {}, true, 'Password')}
        </label>
        <PasswordInput
          value={password}
          handleChange={onChange}
          placeholder="Password"
        />
      </InputGroup>
      {options.country && (
        <InputGroup>
          <label className={CSS.inputLabel}>
            {Translator.translate('Forms.Labels.country', {}, true, 'Country')}
          </label>
          <select
            className={CSS.input}
            name={'country'}
            value={country}
            onChange={onChange}
            required
            placeholder={'Country'}
          >
            <option value="" />
            {renderCountries(countries)}
          </select>
        </InputGroup>
      )}
      {options.phoneNumber && (
        <InputGroup>
          <label className={CSS.inputLabel}>
            {Translator.translate('Forms.Labels.phone', {}, true, 'Phone')}
          </label>
          <div className={CSS.phoneInput}>
            <select
              autoComplete={'tel-country-code'}
              className={classnames(CSS.input, CSS.countryCode)}
              name={'phoneCountryCode'}
              value={phoneCountryCode}
              onChange={onChange}
              onClick={onPhoneCountryCodeClick}
              required
            >
              <CountryCodeSelect countries={countries} />
            </select>
            <span className={CSS.SelectedCode}>
              +{phoneCountryCode?.split('_')?.[0]}
            </span>
            <input
              autoComplete={'tel-national'}
              className={classnames(CSS.input, CSS.phoneNumberInput)}
              type={'phone'}
              value={phoneNumber}
              name={'phoneNumber'}
              onChange={onChange}
              required
            />
          </div>
        </InputGroup>
      )}
      <SubmitButton
        text={Translator.translate('Forms.Buttons.signUp', {}, true, 'Sign up')}
        loading={signupButtonLoading}
      />
    </form>
  );
}

export default SignUpForm;
